"use client";

import { useRailFlyOverContext } from "../../../helpers/RailFlyOverContextProvider";
import {
  CosmosIconExternalLink,
  CosmosText,
  CosmosTitle,
} from "@cosmos/web-react";
import classNames from "classnames/bind";
import styles from "./PromotionalLinkCard.module.css";

const cx = classNames.bind(styles);

export const PromotionalLinkCard = ({
  title,
  kicker,
  linkUrl,
  linkLabel,
  backgroundImageNarrowUrl,
  backgroundImageWideUrl,
  hasProductFlyOver,
}: {
  title: string;
  kicker?: string;
  linkUrl?: string;
  linkLabel?: string;
  backgroundImageNarrowUrl?: string;
  backgroundImageWideUrl?: string;
  hasProductFlyOver?: boolean;
}) => {
  const { show: openRailFlyOver } = useRailFlyOverContext();

  // Either image stands in for the other when an editor only set one
  const narrowUrl = backgroundImageNarrowUrl || backgroundImageWideUrl;
  const wideUrl = backgroundImageWideUrl || backgroundImageNarrowUrl;

  return (
    <div className={cx("container")} data-appearance="light">
      <div className={cx("header")}>
        <h2 className={cx("title")}>
          {(() => {
            const cosmosTitle = (
              <CosmosTitle
                tag="span"
                size="x-small"
                weight="bold"
                appearance="light"
              >
                {title}
              </CosmosTitle>
            );

            // Open a flyover menu, or link to a page
            if (hasProductFlyOver) {
              return (
                <button
                  type="button"
                  className={cx("button")}
                  onClick={openRailFlyOver}
                >
                  {cosmosTitle}
                </button>
              );
            } else if (linkUrl) {
              return (
                /**
                 * NOTE: all links open in new window per client request,
                 * vs:
                 * target={isExternalLink(link ?? "") ? "_blank" : undefined}
                 * rel={isExternalLink(link ?? "") ? "noopener noreferrer" : undefined}
                 */
                <a
                  href={linkUrl}
                  className={cx("link")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cosmosTitle}
                </a>
              );
            } else {
              return cosmosTitle;
            }
          })()}
        </h2>
        <CosmosText
          className={cx("kicker")}
          tag="p"
          size="xx-small"
          weight="medium"
          appearance="light"
          kind="subtle"
        >
          {kicker}
        </CosmosText>
      </div>
      {linkLabel && (
        <CosmosText
          className={cx("link-label")}
          tag="p"
          size="small"
          weight="regular"
          appearance="light"
          kind="subtle"
          clamp={1}
        >
          <CosmosIconExternalLink className={cx("icon")} />
          <span className={cx("link-overflow")}>{linkLabel}</span>
        </CosmosText>
      )}
      {/* 
      <CosmosButton decorative={true} size="small" appearance={appearance}>
        {ctaLabel}
      </CosmosButton> 
      */}
      <div className={cx("gradient")} />
      {narrowUrl && wideUrl && (
        <picture>
          {/* Matches `--breakpoint-medium`, where the card's layout changes */}
          {wideUrl !== narrowUrl && (
            <source media="(width >= 650px)" srcSet={wideUrl} />
          )}
          <img className={cx("background")} src={narrowUrl} alt="" />
        </picture>
      )}
    </div>
  );
};
